/**
 * Shared image upload and type check for camera command and paste handling.
 */

const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

export function isImageFile(file: File): boolean {
  return IMAGE_MIME_TYPES.includes(
    file.type as (typeof IMAGE_MIME_TYPES)[number],
  );
}

export interface UploadImageOptions {
  apiHost: string;
  userEmail: string;
}

export interface UploadImageResult {
  url: string;
  markdown: string;
}

/**
 * Uploads an image file to /api/upload and returns the public URL and markdown snippet.
 * Caller is responsible for toasts and insertion. Throws on validation or HTTP error.
 */
export async function uploadImageFile(
  file: File,
  options: UploadImageOptions,
): Promise<UploadImageResult> {
  if (!isImageFile(file)) {
    throw new Error('File must be an image (JPEG, PNG, GIF, or WebP)');
  }

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${options.apiHost}/api/upload`, {
    method: 'POST',
    body: formData,
    headers: {
      'x-oap-user-email': options.userEmail || '',
    },
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: 'Upload failed' }));
    throw new Error(errorData.message || `HTTP ${response.status}`);
  }

  const result = await response.json();
  const url = result.url as string;
  const altText = file.name.replace(/\.[^/.]+$/, '');
  const markdown = `![${altText}](${url})`;

  return { url, markdown };
}
